import {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenu as DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@bpinternal/ui-kit-next'
import { ComponentProps } from 'react'
import { cn } from '../utils'
import { MenuItem } from './types'

type DropdownMenuProps = Pick<
  ComponentProps<typeof DropdownMenuRoot>,
  'onOpenChange' | 'dir' | 'modal' | 'defaultOpen' | 'open'
> &
  Pick<ComponentProps<typeof DropdownMenuTrigger>, 'disabled' | 'children'> &
  Omit<ComponentProps<typeof DropdownMenuContent>, 'content'> & {
    content: MenuItem[]
    size?: '1' | '2' | '3' | '4'
  }

export const DropdownMenu = ({
  children,
  disabled,
  onOpenChange,
  dir,
  modal,
  content,
  open,
  defaultOpen,
  size = '2',
  ...props
}: DropdownMenuProps) => {
  const disabledState = disabled || !content.filter((t) => !t.hidden).length

  return (
    <DropdownMenuRoot {...{ onOpenChange, dir, modal, open, defaultOpen }}>
      <DropdownMenuTrigger asChild disabled={disabledState} className={cn({ 'cursor-pointer': !disabled })}>
        {!content.filter((t) => !t.hidden).length ? null : children}
      </DropdownMenuTrigger>
      <DropdownMenuContent {...props} className={cn(size === '2' ? 'p-2' : 'p-1', props.className)}>
        <DropdownMenuItems content={content} size={size} />
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}

const DropdownMenuItems = ({ content, size }: { content: MenuItem[]; size: '1' | '2' | '3' | '4' }) => {
  const getBaseClassName = (danger?: boolean) =>
    cn(
      'cursor-pointer',
      { 'gap-2': size === '2', 'gap-1.5': size === '1' },
      danger && 'text-destructive focus:bg-destructive/25 focus:text-destructive'
    )
  return (
    <>
      {content.map((item, i) => {
        if (item.hidden) {
          return null
        }
        switch (item.type) {
          case 'item': {
            const { content: itemContent, leadingIcon, trailingIcon, danger, ...itemProps } = item
            return (
              <DropdownMenuItem className={getBaseClassName(danger)} {...itemProps} key={i}>
                {leadingIcon}
                <span className="grow">{itemContent}</span>
                {trailingIcon}
              </DropdownMenuItem>
            )
          }
          case 'separator':
            return <DropdownMenuSeparator className={cn({ 'mx-3': size === '2', 'mx-2': size === '1' })} key={i} />
          case 'label':
            return (
              <DropdownMenuLabel className={getBaseClassName()} key={i}>
                {item.label}
              </DropdownMenuLabel>
            )
          case 'radioGroup':
            return (
              <DropdownMenuRadioGroup key={i} value={item.value} onValueChange={item.onValueChange}>
                {item.items.map((radioItem, j) => {
                  const { content: itemContent, leadingIcon, trailingIcon, danger, ...itemProps } = radioItem
                  return (
                    <DropdownMenuRadioItem className={getBaseClassName(danger)} key={j} {...itemProps}>
                      {leadingIcon}
                      <span>{itemContent}</span>
                      {trailingIcon}
                    </DropdownMenuRadioItem>
                  )
                })}
              </DropdownMenuRadioGroup>
            )
          case 'submenu':
            return (
              <DropdownMenuSub key={i}>
                <DropdownMenuSubTrigger className={getBaseClassName()} disabled={item.disabled}>
                  <div className="flex items-center gap-2">
                    {item.leadingIcon}
                    <span>{item.content}</span>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItems content={item.items} size={size} />
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )
          case 'checkBoxItem': {
            const { content: itemContent, leadingIcon, trailingIcon, showCheckmark, danger, ...itemProps } = item
            return (
              <DropdownMenuCheckboxItem
                key={i}
                className={cn(
                  getBaseClassName(danger),
                  { '[&>span:first-child]:hidden': !showCheckmark && showCheckmark !== undefined },
                  '*:relative'
                )}
                {...itemProps}
              >
                {leadingIcon}
                <span className="grow">{itemContent}</span>
                {trailingIcon}
              </DropdownMenuCheckboxItem>
            )
          }
          default:
            return <></>
        }
      })}
    </>
  )
}
